import { cloneDeep, isObject } from "lodash";

const deProxy = (o: unknown): unknown => {
    if (Array.isArray(o)) {
        return Array.from(o).map(deProxy);
    }

    if (isObject(o)) {
        return cloneDeep(o);
    }

    return o;
};

type BaseDataProperty = keyof Data;

export type DataProperty<T> = Exclude<keyof T, BaseDataProperty>;

export type DataPayload<T> = {
    [K in DataProperty<T>]?: T[K] extends infer P extends Data ? DataPayload<P> : T[K];
};

export type BaseData<T> = Partial<T> & {
    from(data: DataPayload<T>): T;
};

const emptyCache = new WeakMap();
const originalCache = new WeakMap();

abstract class Data {
    static from<T extends Data, D extends DataPayload<T>>(this: new (...args: never[]) => T, data: D): T {
        // biome-ignore lint/complexity/noThisInStatic: this binds to the child class instead of Data
        return Object.seal((Reflect.construct(this.prototype.constructor, []) as T).fill(data, true));
    }

    public getEmpty(): Partial<this> {
        const target = this.constructor;

        if (!emptyCache.has(target)) {
            emptyCache.set(target, Reflect.construct(target, []));
        }

        return emptyCache.get(target);
    }

    get fields(): DataProperty<this>[] {
        return Object.getOwnPropertyNames(this).filter((field) => !field.startsWith("#")) as DataProperty<this>[];
    }

    public sync(): this {
        originalCache.set(this, this.all());

        return this;
    }

    get original(): Partial<this> {
        if (!originalCache.has(this)) {
            this.sync();
        }

        return originalCache.get(this) as Partial<this>;
    }

    public fill(data: DataPayload<this>, sync = false): this {
        const existing = this.all();
        const empty = this.getEmpty();

        for (const prop of this.fields) {
            let value = deProxy(Object.hasOwn(data, prop) ? data[prop] : existing[prop]);

            if (empty[prop] instanceof Data || existing[prop] instanceof Data) {
                const record = (existing[prop] instanceof Data ? existing[prop] : empty[prop]) as Data &
                    (typeof this)[typeof prop];

                value = (record.constructor as unknown as BaseData<Data>).from(value as DataPayload<Data>);
            }

            Reflect.set(this, prop, value);
        }

        if (sync) {
            this.sync();
        }

        return this;
    }

    public reset(fields?: DataProperty<this> | DataProperty<this>[]): this {
        const reset = Object.keys(this.only(fields ?? this.fields)) as DataProperty<this>[];
        const original = this.original;

        for (const field of reset) {
            Reflect.set(this, field, original[field] ?? undefined);
        }

        return this;
    }

    public filled(fields?: DataProperty<this> | DataProperty<this>[]): boolean {
        const items = this.only(fields ?? this.fields);
        const empty = this.getEmpty();

        for (const field in items) {
            if (items[field] === empty[field]) {
                return false;
            }
        }

        return true;
    }

    public empty(fields?: DataProperty<this> | DataProperty<this>[]): boolean {
        const items = this.only(fields ?? this.fields);
        const empty = this.getEmpty();

        for (const field in items) {
            if (items[field] !== empty[field]) {
                return false;
            }
        }

        return true;
    }

    public dirty(fields?: DataProperty<this> | DataProperty<this>[]): boolean {
        const items = this.only(fields ?? this.fields);
        const original = this.original;
        for (const field in items) {
            if (JSON.stringify(items[field] ?? null) === JSON.stringify(original[field] ?? null)) {
                continue;
            }

            return true;
        }

        return false;
    }

    public only(keys: DataProperty<this> | DataProperty<this>[]): Partial<this> {
        const find = (Array.isArray(keys) ? keys : [keys]).filter((key) =>
            this.fields.includes(key),
        ) as DataProperty<this>[];

        const only = {} as Partial<this>;

        for (const key of find) {
            Reflect.set(only, key, deProxy(this[key]));
        }

        return only;
    }

    public all(): Partial<this> {
        return this.only(this.fields);
    }
}

export default Data;
